import React from "react";
import { Card, Carousel } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useScanQrcodeStore } from "@/store";
import { isEmpty, formatCurrency } from "@/utils";
import type { IProduct } from "@/types";

interface ProductSwiperProps {
  className?: string;
}

// Custom Arrow Components
const CustomPrevArrow = ({ onClick }: { onClick?: () => void }) => (
  <button
    onClick={onClick}
    className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white rounded-full shadow-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 hover:shadow-xl transition-all duration-200 text-gray-600 hover:text-gray-800"
    style={{ zIndex: 10 }}
  >
    <LeftOutlined className="text-lg" />
  </button>
);

const CustomNextArrow = ({ onClick }: { onClick?: () => void }) => (
  <button
    onClick={onClick}
    className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white rounded-full shadow-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 hover:shadow-xl transition-all duration-200 text-gray-600 hover:text-gray-800"
    style={{ zIndex: 10 }}
  >
    <RightOutlined className="text-lg" />
  </button>
);

const ProductSwiper: React.FC<ProductSwiperProps> = ({ className = "" }) => {
  const { t } = useTranslation();
  const { product } = useScanQrcodeStore();

  if (isEmpty(product?.related_products) || !product?.related_products?.length) {
    return null;
  }

  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    arrows: true,
    prevArrow: <CustomPrevArrow />,
    nextArrow: <CustomNextArrow />,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  const handleProductClick = (relatedProduct: IProduct) => {
    if (relatedProduct.url) {
      // Open URL in new tab/window
      window.open(relatedProduct.url, "_blank", "noopener,noreferrer");
    }
  };

  const renderProductCard = (relatedProduct: IProduct) => (
    <div key={relatedProduct.id} className="p-2">
      <Card
        hoverable
        cover={
          relatedProduct.image ? (
            <img alt={relatedProduct.name} src={relatedProduct.image} className="h-48 object-cover" />
          ) : (
            <div className="h-48 bg-gray-200 flex items-center justify-center">
              <span className="text-gray-500">{t("common.no_image")}</span>
            </div>
          )
        }
        className="h-full"
        onClick={() => handleProductClick(relatedProduct)}
      >
        <Card.Meta
          title={<div className="font-medium truncate text-center">{relatedProduct.name}</div>}
          description={
            <div className="space-y-1">
              {relatedProduct.brand && <p className="text-gray-500 truncate">{relatedProduct.brand}</p>}
              {relatedProduct.price > 0 && (
                <p className="text-center font-semibold qrx-text--success">{formatCurrency(relatedProduct.price)}</p>
              )}
            </div>
          }
        />
      </Card>
    </div>
  );

  return (
    <Card
      className={className}
      title={t("common.related_products")}
      styles={{ title: { color: "var(--color-warning)", fontWeight: 600 } }}
    >
      <div className="relative">
        <Carousel {...settings}>{product.related_products.map(renderProductCard)}</Carousel>
      </div>
    </Card>
  );
};

export default ProductSwiper;
